import { Icon } from "@dmm-com/rocket-web-ui-library";
import { memo, useMemo } from "react";
import { useApplicationHandler } from "../hooks/use-application-handler";
import { openShareDialog } from "../utils/open-share-dialog";
import * as Styled from "./style";
import { useDesktopActionButton } from "./use-desktop-action-button";
import { useRouter } from "next/router";
import { useDeleteBookmark } from "~/components/blocks/my/bookmark/my-bookmark-infinity-scroll/hooks/use-delete-bookmark";
import { isVideoLegacySeason, isVideoSeason } from "~/components/blocks/product/type-guard";
import { openDialog } from "~/components/stores/dialog/store";
import { openTextOnlyToast } from "~/components/stores/toast/store";
import { isTouchDisplay } from "~/const/device";
import { PATHS } from "~/const/paths";
import { useOutsidePointerdown } from "~/hooks/use-outside-pointerdown";
import { useQueryString } from "~/hooks/use-query-string";
import { useRatingBlock } from "~/hooks/use-rating-block";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { G_VideoContentType, G_VideoSeasonType } from "~/lib/graphql";
import { platform } from "~/lib/navigator/user-agent";
import { useAuth } from "~/provider/auth";
import { removeTrailingEndSlash } from "~/util/remove-trailing-end-slash";

type DesktopActionButtonsProps = { videoData: G_GetVideoQuery["video"] };

/** 画面幅が「600px以上」の場合に表示するアクションボタンブロックコンポーネント */
export const DesktopActionButtons = memo((props: DesktopActionButtonsProps) => {
  const { videoData } = props;

  const { season, content, spot } = useQueryString();
  const { isLoggedIn, execAuth } = useAuth();
  const router = useRouter();

  const {
    shareIconList,
    isFavoriteRegistered,
    isOpenTooltip,
    addBookmarkHandler,
    deleteBookmarkHandler,
    onDownloadBtnClicked,
    onTooltipClose,
    tooltipToggleHandler,
  } = useDesktopActionButton({ videoData });

  const {
    applicationDmmTvInstall,
    applicationDmmTvScheme,
    application2DInstall,
    application2DScheme,
    applicationVrInstall,
    applicationVrScheme,
  } = useApplicationHandler({ videoData });

  const { ref: shareButtonRef } = useOutsidePointerdown<HTMLDivElement>({
    onOutsidePointerDown: () => {
      if (!isOpenTooltip) return;
      onTooltipClose();
    },
  });

  const handleFavoriteBtnPressed = () => {
    if (!isLoggedIn) {
      openDialog({
        title: "お気に入り登録は\nログイン後にできます",
        mainButtonName: "ログイン",
        mainButtonLabelText: "ログイン",
        description: "ログインしますか？",
        isDisabledAnimation: true,
        onMainButtonAction: execAuth,
      });

      return;
    }

    if (isFavoriteRegistered) {
      deleteBookmarkHandler();
    } else {
      addBookmarkHandler();
    }
  };

  const handlePlayWithApplicationBtnPressed = () => {
    if (isVideoLegacySeason(videoData)) {
      const {
        contentType,
        viewingRights: { iosURLSchemes, androidURLSchemes },
      } = videoData.content;

      const isVod2D = contentType === G_VideoContentType.Vod_2D;
      const isVodVr = contentType === G_VideoContentType.VodVr;

      const dialogTitle = isVod2D
        ? "グラビア動画をアプリで再生するには\nDMM動画プレイヤーが必要です"
        : "VR動画をアプリで再生するには\nDMM VR動画プレイヤーが必要です";

      const dialogDescription = isVod2D ? "アプリでの再生は\n「レンタル・購入」した作品のみ対応しています" : undefined;

      const isIosVr =
        isVodVr &&
        platform === "iOS" &&
        iosURLSchemes !== undefined &&
        iosURLSchemes !== null &&
        iosURLSchemes.length > 0;

      const isAndroidVr =
        isVodVr &&
        platform === "Android" &&
        androidURLSchemes !== undefined &&
        androidURLSchemes !== null &&
        androidURLSchemes.length > 0;

      openDialog({
        title: dialogTitle,
        description: dialogDescription,
        mainButtonName: "アプリをインストール",
        mainButtonLabelText: "アプリをインストール",
        onMainButtonAction: isVod2D ? application2DInstall : applicationVrInstall,
        isDisabledAnimation: true,
        subButtonName: isVod2D || isIosVr || isAndroidVr ? "アプリを起動" : undefined,
        subButtonLabelText: isVod2D || isIosVr || isAndroidVr ? "アプリを起動" : undefined,
        onSubButtonAction: isVod2D ? application2DScheme : applicationVrScheme,
        imageSrc: isVod2D ? "/images/appIcon_dmmplayer.png" : "/images/appIcon_dmmvrplayer.png",
      });

      return;
    }

    openDialog({
      title: "この動画をアプリで再生するには\nDMM TVアプリが必要です",
      mainButtonName: "アプリをインストール",
      mainButtonLabelText: "アプリをインストール",
      onMainButtonAction: applicationDmmTvInstall,
      isDisabledAnimation: true,
      subButtonName: "アプリを起動",
      subButtonLabelText: "アプリを起動",
      onSubButtonAction: () => applicationDmmTvScheme(season, content),
      imageSrc: "/images/appIcon_dmmtv.png",
    });
  };

  const handleDownloadBtnPressed = () => {
    const downloadModalData = {
      titleName: videoData.titleName,
      seasonType: videoData.seasonType,
      downloadableFiles,
      contentType: isVideoLegacySeason(videoData) ? videoData.content.contentType : undefined,
    };
    onDownloadBtnClicked(contentIdForDownloadModal ?? "", downloadModalData);
  };

  const handleShareBtnPressed = () => {
    if (isTouchDisplay) {
      openShareDialog({ videoData, spot });
    } else {
      tooltipToggleHandler();
    }
  };

  const downloadableFiles = useMemo(() => {
    if (isVideoSeason(videoData)) {
      if (videoData.episodes.edges.length === 0) return;
      if (!videoData.episodes.edges[0].node.viewingRights.isDownloadable) return;

      const {
        viewingRights: { downloadableFiles },
      } = videoData.episodes.edges[0].node;

      return downloadableFiles;
    }

    if (isVideoLegacySeason(videoData)) {
      if (!videoData.content.viewingRights.isDownloadable) return;

      const {
        viewingRights: { downloadableFiles },
      } = videoData.content;

      return downloadableFiles;
    }

    return;
  }, [videoData]);

  /** ダウンロードモーダルを開くときに必要な`contentId` */
  const contentIdForDownloadModal = useMemo(() => {
    if (isVideoSeason(videoData)) {
      // edgesが「空配列」の場合にここで処理を止めたいので早期returnを挟む
      if (videoData.episodes.edges.length === 0) return;
      const { id } = videoData.episodes.edges[0].node;
      return id;
    }

    if (isVideoLegacySeason(videoData)) {
      const { id } = videoData.content;
      return id;
    }

    return;
  }, [videoData]);

  const isMyBookmark = router.pathname === removeTrailingEndSlash(PATHS.MY_BOOKMARK);

  const { isRestrictedContentByUserStatus } = useRatingBlock();

  const { isDeletedById, deleteBookmark, restoreBookmark } = useDeleteBookmark();

  const isPendingRemoval = isDeletedById(videoData.id);

  const handleBookmarkActionOnBookmarkPage = () => {
    const action = isFavoriteRegistered && !isPendingRemoval ? "DELETE" : "ADD";
    action === "DELETE" ? deleteBookmark(videoData.id) : restoreBookmark(videoData.id);
    openTextOnlyToast(`${action}_BOOKMARK`);
  };

  return (
    <Styled.DivPcAndTabletRoot>
      {!isRestrictedContentByUserStatus(videoData.rating.category, true) && (
        <Styled.ButtonFavorite
          type="button"
          name="Favorite Button"
          aria-label="お気に入りボタン"
          onClick={isMyBookmark && isFavoriteRegistered ? handleBookmarkActionOnBookmarkPage : handleFavoriteBtnPressed}
        >
          <Styled.PcIcon
            name={isFavoriteRegistered && !isPendingRemoval ? "check" : "signPlus"}
            color="GRAY50"
            size="XL"
          />

          {/** FIXME（kimura）: Mutationのリクエストに失敗してもアニメーションが動いてしまうため「UI Library」側の修正必要 */}
          {/* <AnimationIcon
            isActive={isFavoriteRegistered}
            name="favoriteRegister"
            iconColor="GRAY200"
            iconSize="XXL"
          /> */}

          <Styled.ParIconName>お気に入り</Styled.ParIconName>
        </Styled.ButtonFavorite>
      )}

      {isTouchDisplay && !isRestrictedContentByUserStatus(videoData.rating.category, true) && (
        <Styled.ButtonPlayWithApplication
          type="button"
          name="Play Button With Application"
          aria-label="アプリで再生ボタン"
          onClick={handlePlayWithApplicationBtnPressed}
        >
          <Styled.PcIcon name="blank" color="GRAY50" size="XL" />
          <Styled.ParIconName>アプリで再生</Styled.ParIconName>
        </Styled.ButtonPlayWithApplication>
      )}

      {!isRestrictedContentByUserStatus(videoData.rating.category, true) &&
        (() => {
          // MEMO : 複数話・舞台・スポット配信の場合、DLボタンを非表示にすること
          if (
            [G_VideoSeasonType.MultiEpisodes, G_VideoSeasonType.Stage, G_VideoSeasonType.SpotLive].includes(
              videoData.seasonType
            )
          )
            return null;

          // MEMO : 画面幅が600px以上の「iOS / iPadOS / Android」の場合、DLボタンを非表示にすること
          if (isTouchDisplay) return null;

          // MEMO : 単数話（SINGLE_EPISODE）でダウンロード許諾がない場合、DLボタンを非表示にすること
          if (isVideoSeason(videoData)) {
            if (videoData.episodes.edges.length === 0) return null;
            if (!videoData.episodes.edges[0].node.viewingRights.isDownloadable) return null;
          }

          // MEMO : LEGACY作品でダウンロード許諾がない場合、DLボタンを非表示にすること
          if (isVideoLegacySeason(videoData)) {
            if (!videoData.content.viewingRights.isDownloadable) return null;
          }

          // MEMO: 視聴制限がある場合はDLボタンを非表示にすること
          if (isRestrictedContentByUserStatus(videoData.rating.category, true)) return null;

          // MEMO : 未ログイン または 単数話の視聴権なし または グラビアの視聴権なしの場合、ボタンを非活性にすること
          const isBtnDisabled =
            !isLoggedIn ||
            (isVideoSeason(videoData) &&
              videoData.episodes.edges.length > 0 &&
              !videoData.episodes.edges[0].node.viewingRights.isStreamable) ||
            (isVideoLegacySeason(videoData) && !videoData.content.viewingRights.isStreamable);

          return (
            <Styled.ButtonDownload
              type="button"
              name="DownloadModal Open Button"
              aria-label="ダウンロードモーダルを開くボタン"
              disabled={isBtnDisabled}
              onClick={handleDownloadBtnPressed}
            >
              <Styled.PcIcon name="download" color="GRAY50" size="XL" />
              <Styled.ParIconName>ダウンロード</Styled.ParIconName>
            </Styled.ButtonDownload>
          );
        })()}

      <Styled.DivButtonShare
        role="button"
        aria-label="シェアボタン"
        tabIndex={0}
        ref={shareButtonRef}
        onKeyDown={handleShareBtnPressed}
        onClick={handleShareBtnPressed}
      >
        <Styled.PcIcon name="share" color="GRAY50" size="XL" />
        <Styled.ParIconName>シェア</Styled.ParIconName>

        {isOpenTooltip && (
          <Styled.Tooltip2
            positionTop="88px"
            positionRight="0px"
            backgroundColor="GRAY200"
            fontSize="XXS"
            arrowDirection="top"
            arrowRight={17}
          >
            {shareIconList.map(({ name, icon, onIconClicked }) => {
              return (
                <Styled.ButtonShare
                  type="button"
                  key={name}
                  name={name}
                  aria-label={name}
                  onClick={() => onIconClicked(videoData.titleName)}
                >
                  <Icon name={icon} size="XXXL" color={icon === "copyLink" ? "GRAY900" : undefined} />
                  <Styled.ParIconName>{name}</Styled.ParIconName>
                </Styled.ButtonShare>
              );
            })}
          </Styled.Tooltip2>
        )}
      </Styled.DivButtonShare>
    </Styled.DivPcAndTabletRoot>
  );
});

DesktopActionButtons.displayName = "DesktopActionButtons";
